import * as THREE from 'three'
import { OPACIDADE_DAS_ARESTAS_EM_FANTASMA } from './ghostContornos'

// ============================================================================
// edgeVisibility: per-item edge filtering via shader
//
// Edges are generated per-mesh in useViewer (THREE.EdgesGeometry), but
// Fragments hides items per-item via shader. Result: hiding a wall
// leaves its outline floating as phantom wireframe. To fix that we:
//   * Bake a per-vertex `id` attribute on each LineSegments geometry
//     (matches the source geometry's per-vertex `id` attribute, i.e.
//     Fragments' internal itemIndex, NOT localId).
//   * Patch each edge LineBasicMaterial via onBeforeCompile to read a
//     per-model R8 visibility DataTexture indexed by id; fragments
//     where visibility == 0 are discarded.
//   * Update the texture when item visibility changes.
//
// All ids in this module are itemIndices. Callers using localIds must
// translate via `model.threads.invoke(modelId, "getItemIdsByLocalIds")`
// before calling setItemEdgeVisibility.
// ============================================================================

const TEX_SIDE = 1024
const TEX_SLOTS = TEX_SIDE * TEX_SIDE

/** Valor de uma casa da textura: escondido, esbatido (fantasma por
 *  contornos, ver ghostContornos.ts) ou visível. */
const ESCONDIDO = 0
const ESBATIDO = 128
const VISIVEL = 255

interface EdgeVisState {
  modelId: string
  /** R-channel only: 0 = hidden, 128 = faded (ghost outline), 255 = visible. */
  pixels: Uint8Array
  texture: THREE.DataTexture
  patchedMaterials: WeakSet<THREE.Material>
  /** Items whose edges we always discard, regardless of Fragments'
   *  per-item visibility state. Typically spatial containers
   *  (IfcSpace, IfcBuildingStorey, etc.) whose semi-transparent shells
   *  are part of the IFC but whose outlines look like floating
   *  wireframes to the user. */
  permanentlyHidden: Set<number>
}

const states = new Map<string, EdgeVisState>()

function ensureState(modelId: string): EdgeVisState {
  let s = states.get(modelId)
  if (s) return s
  // Default everything to visible (255). Items the user hides later
  // get flipped to 0 via setItemEdgeVisibility.
  const pixels = new Uint8Array(TEX_SLOTS).fill(255)
  const texture = new THREE.DataTexture(
    pixels,
    TEX_SIDE,
    TEX_SIDE,
    THREE.RedFormat,
    THREE.UnsignedByteType,
  )
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.flipY = false
  texture.needsUpdate = true
  s = {
    modelId,
    pixels,
    texture,
    patchedMaterials: new WeakSet(),
    permanentlyHidden: new Set(),
  }
  states.set(modelId, s)
  return s
}

/** Set the items that should NEVER show edges, regardless of
 *  Fragments-per-item visibility. Call once per model when the
 *  spatial-container item set is known. Texture is updated
 *  immediately so existing meshes pick up the change next frame. */
/** A textura de um modelo (0 escondido, 128 em fantasma, 255 visível).
 *  O shader das faces (paintEngine) lê-a para descartar as faces dos
 *  elementos em fantasma: o mesmo índice por elemento que as arestas. */
export function texturaDaVisibilidade(modelId: string): THREE.DataTexture {
  return ensureState(modelId).texture
}

export function setPermanentlyHidden(
  modelId: string,
  itemIndices: Iterable<number>,
): void {
  const s = ensureState(modelId)
  s.permanentlyHidden = new Set(itemIndices)
  // Apply right away on top of whatever is currently in pixels.
  for (const idx of s.permanentlyHidden) {
    if (idx >= 0 && idx < TEX_SLOTS) s.pixels[idx] = 0
  }
  s.texture.needsUpdate = true
}

/** Mark each item index in `hiddenItemIndices` as hidden (0), each in
 *  `fadedItemIndices` as faded (the ghost's outline-only items) and
 *  every other slot as visible (255). Single full refresh: call when
 *  the set of hidden items changed via Fragments setVisible. */
export function refreshVisibilityFor(
  modelId: string,
  hiddenItemIndices: Iterable<number>,
  fadedItemIndices: Iterable<number> = [],
): void {
  const s = ensureState(modelId)
  s.pixels.fill(VISIVEL)
  for (const idx of fadedItemIndices) {
    if (idx >= 0 && idx < TEX_SLOTS) s.pixels[idx] = ESBATIDO
  }
  // OR the per-frame hidden set with the model's permanent-hidden set
  // so spatial containers' edges never show regardless of Fragments'
  // own visibility state.
  for (const idx of hiddenItemIndices) {
    if (idx >= 0 && idx < TEX_SLOTS) s.pixels[idx] = ESCONDIDO
  }
  for (const idx of s.permanentlyHidden) {
    if (idx >= 0 && idx < TEX_SLOTS) s.pixels[idx] = ESCONDIDO
  }
  s.texture.needsUpdate = true
}

/** Opacidade das arestas esbatidas, a mesma para todos os modelos. */
const opacidadeEsbatida = { value: OPACIDADE_DAS_ARESTAS_EM_FANTASMA }
export function setFadedEdgeOpacity(value: number): void {
  opacidadeEsbatida.value = Math.min(1, Math.max(0, value))
}

/** Patch a LineBasicMaterial so edge fragments discard themselves when
 *  the item's visibility slot is 0. Safe to call repeatedly: the
 *  WeakSet guards against double-injection. */
export function patchEdgeMaterial(
  material: THREE.Material,
  modelId: string,
): void {
  const state = ensureState(modelId)
  if (state.patchedMaterials.has(material)) return
  const matType =
    (material as THREE.Material & { type?: string }).type ?? ''
  const isLine =
    matType === 'LineBasicMaterial' ||
    !!(material as THREE.LineBasicMaterial & {
      isLineBasicMaterial?: boolean
    }).isLineBasicMaterial
  if (!isLine) return
  state.patchedMaterials.add(material)

  const prev = material.onBeforeCompile
  material.onBeforeCompile = (shader, renderer) => {
    if (prev) prev(shader, renderer)
    shader.uniforms.vn2rEdgeVisTex = { value: state.texture }
    shader.uniforms.vn2rEdgeVisSide = { value: TEX_SIDE }
    shader.uniforms.vn2rEdgeFade = opacidadeEsbatida

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float id;
flat varying float vVn2rEdgeId;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
vVn2rEdgeId = id;`,
      )

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform sampler2D vn2rEdgeVisTex;
uniform float vn2rEdgeVisSide;
uniform float vn2rEdgeFade;
flat varying float vVn2rEdgeId;`,
      )
      .replace(
        '#include <clipping_planes_fragment>',
        `#include <clipping_planes_fragment>
float vn2rAlphaDaAresta = 1.0;
{
  float idx = vVn2rEdgeId;
  float xi = mod(idx, vn2rEdgeVisSide);
  float yi = floor(idx / vn2rEdgeVisSide);
  vec2 uv = (vec2(xi, yi) + 0.5) / vn2rEdgeVisSide;
  float vis = texture2D(vn2rEdgeVisTex, uv).r;
  if (vis < 0.25) discard;
  if (vis < 0.75) vn2rAlphaDaAresta = vn2rEdgeFade;
}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
diffuseColor.a *= vn2rAlphaDaAresta;`,
      )
  }
  ;(material as THREE.Material & {
    customProgramCacheKey?: () => string
  }).customProgramCacheKey = () => `vn2rEdgeVis2:${modelId}`
  // Para as arestas esbatidas o alfa conta; nas outras fica 1.
  material.transparent = true
  // Mark via userData so the debug helper can confirm the patch is in
  // place from outside this module.
  if (!material.userData) material.userData = {}
  ;(material.userData as { vn2rEdgePatched?: boolean }).vn2rEdgePatched =
    true
  material.needsUpdate = true
}

/** Build a per-edge `id` attribute by mapping each edge endpoint
 *  position back to the source vertex that produced it. Returns a
 *  Float32Array (length = edge vertex count). */
export function buildEdgeIdAttribute(
  edgePositions: Float32Array,
  sourcePositions: Float32Array,
  sourceIds: Float32Array,
): Float32Array {
  // Build a position-key → source vertex index lookup. Key is the raw
  // x|y|z floats concatenated as bits, collision-free for the source
  // vertex set, fast to compute, no string allocation per lookup.
  const lookup = new Map<bigint, number>()
  const sourceVtxCount = sourcePositions.length / 3
  for (let i = 0; i < sourceVtxCount; i++) {
    const k = posKey(
      sourcePositions[i * 3],
      sourcePositions[i * 3 + 1],
      sourcePositions[i * 3 + 2],
    )
    if (!lookup.has(k)) lookup.set(k, i)
  }
  const edgeVtxCount = edgePositions.length / 3
  const out = new Float32Array(edgeVtxCount)
  for (let i = 0; i < edgeVtxCount; i++) {
    const k = posKey(
      edgePositions[i * 3],
      edgePositions[i * 3 + 1],
      edgePositions[i * 3 + 2],
    )
    const srcIdx = lookup.get(k)
    out[i] = srcIdx !== undefined ? sourceIds[srcIdx] : 0
  }
  return out
}

// Pack 3 float32s into a single bigint key. We just reinterpret each
// float as its 32-bit integer representation and shift; collisions
// require all three bits to coincide, which doesn't happen for
// distinct vertex positions.
const _f32 = new Float32Array(1)
const _u32 = new Uint32Array(_f32.buffer)
function bitsOf(f: number): number {
  _f32[0] = f
  return _u32[0]
}
function posKey(x: number, y: number, z: number): bigint {
  return (
    (BigInt(bitsOf(x)) << 64n) |
    (BigInt(bitsOf(y)) << 32n) |
    BigInt(bitsOf(z))
  )
}

/** Forget all state for a model that's about to be unloaded. */
export function disposeEdgeVisibility(modelId: string): void {
  const s = states.get(modelId)
  if (!s) return
  s.texture.dispose()
  states.delete(modelId)
}
